/**
 * CP-12.2 — Contractor Qualification Wizard.
 *
 * Multi-step contractor profile re-shape covering the Phase 12 qualification
 * taxonomy:
 *   1. Tracks (primary + secondary)  — Lock §27 + §35
 *   2. Segments                       — within chosen tracks
 *   3. Roles                          — track-scoped, primary flag
 *   4. Equipment                      — Lock §28 first-class; per-item tier + subjective
 *   5. Certifications                 — issued_at / expires_at / credential id
 *
 * Writes only to the _self layer of the 3-layer storage (Lock §32). Verification
 * is forbidden from this surface (CP-12.5 territory).
 */
import React, { useEffect, useMemo, useState } from "react";
import { Layout } from "@/components/Layout";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { ContractorLocationForm, ContractorTravelForm } from "@/pages/MarketplaceSearch";
import { MyVerificationsPanel } from "@/pages/Verifications";

const STEPS = [
  { id: "tracks",   label: "1. Industry Tracks" },
  { id: "segments", label: "2. Segments" },
  { id: "roles",    label: "3. Roles" },
  { id: "equipment", label: "4. Equipment" },
  { id: "certs",    label: "5. Certifications" },
  { id: "location", label: "6. Location & Travel" },
  { id: "verif",    label: "7. Verifications" },
];

const TIERS = ["1-2", "5+", "20+", "100+"];
const SUBJECTIVE = ["familiar", "intermediate", "advanced", "expert"];

export default function ContractorQualification() {
  const { user } = useAuth();
  const [step, setStep] = useState("tracks");
  const [tracks, setTracks] = useState([]);
  const [segments, setSegments] = useState([]);
  const [roles, setRoles] = useState([]);
  const [equipmentTags, setEquipmentTags] = useState([]);
  const [certifications, setCertifications] = useState([]);

  // Contractor state
  const [myTracks, setMyTracks] = useState({ primary: null, secondary: [] });
  const [mySegments, setMySegments] = useState([]);
  const [myRoles, setMyRoles] = useState([]);
  const [myEquipment, setMyEquipment] = useState([]);
  const [myCerts, setMyCerts] = useState([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Initial load
  useEffect(() => {
    (async () => {
      try {
        const [
          tracksRes, segsRes, rolesRes, eqRes, certRes,
          myT, myS, myR, myE, myC,
        ] = await Promise.all([
          api("/admin/catalogs/tracks"),
          api("/admin/catalogs/segments"),
          api("/admin/catalogs/roles"),
          api("/admin/catalogs/equipment-tags"),
          api("/admin/catalogs/qualification-certifications"),
          api("/me/contractor-profile/qualification/tracks"),
          api("/me/contractor-profile/qualification/segments"),
          api("/me/contractor-profile/qualification/roles"),
          api("/me/contractor-profile/qualification/equipment"),
          api("/me/contractor-profile/qualification/certifications"),
        ]);
        setTracks(tracksRes.data);
        setSegments(segsRes.data);
        setRoles(rolesRes.data);
        setEquipmentTags(eqRes.data);
        setCertifications(certRes.data);
        setMyTracks(myT);
        setMySegments(myS.data.map((s) => s.segment_id));
        setMyRoles(myR.data.map((r) => ({
          role_id: r.role_id, is_primary: r.is_primary,
          years_in_role: r.years_in_role,
        })));
        setMyEquipment(myE.data.map((e) => ({
          equipment_tag_id: e.equipment_tag_id,
          project_count_tier: e.project_count_tier,
          subjective_level: e.subjective_level,
        })));
        setMyCerts(myC.data.map((c) => ({
          certification_id: c.certification_id,
          issued_at: c.issued_at, expires_at: c.expires_at,
          credential_id: c.credential_id, verification_url: c.verification_url,
        })));
      } catch (e) {
        if (e?.status === 403) {
          toast.error("Contractor role required to use the qualification wizard.");
        } else {
          toast.error(e?.message || "Failed to load qualification data");
        }
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <Layout>
        <div className="p-6" data-testid="cp12-qual-loading">Loading…</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-5xl mx-auto p-6" data-testid="cp12-qual-page">
        <header className="mb-6">
          <h1 className="text-3xl font-semibold" data-testid="cp12-qual-title">
            Professional Qualification
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Welcome, {user?.email}. These claims will be the basis for how Clients
            discover you. Self-declared today; verifications can be earned over time.
          </p>
        </header>

        <nav className="mb-6 flex gap-2 flex-wrap" data-testid="cp12-qual-steps">
          {STEPS.map((s) => (
            <button
              key={s.id}
              onClick={() => setStep(s.id)}
              className={
                "px-3 py-1.5 rounded-md text-sm " +
                (step === s.id
                  ? "bg-slate-900 text-white"
                  : "bg-slate-100 text-slate-700 hover:bg-slate-200")
              }
              data-testid={`cp12-qual-step-${s.id}`}
            >
              {s.label}
            </button>
          ))}
        </nav>

        {step === "tracks" && (
          <TracksStep
            tracks={tracks}
            myTracks={myTracks}
            saving={saving}
            setSaving={setSaving}
            onSaved={setMyTracks}
          />
        )}
        {step === "segments" && (
          <SegmentsStep
            segments={segments}
            tracks={tracks}
            mySegments={mySegments}
            saving={saving}
            setSaving={setSaving}
            onSaved={setMySegments}
          />
        )}
        {step === "roles" && (
          <RolesStep
            roles={roles}
            tracks={tracks}
            myRoles={myRoles}
            saving={saving}
            setSaving={setSaving}
            onSaved={setMyRoles}
          />
        )}
        {step === "equipment" && (
          <EquipmentStep
            equipmentTags={equipmentTags}
            myEquipment={myEquipment}
            saving={saving}
            setSaving={setSaving}
            onSaved={setMyEquipment}
          />
        )}
        {step === "certs" && (
          <CertificationsStep
            certifications={certifications}
            myCerts={myCerts}
            saving={saving}
            setSaving={setSaving}
            onSaved={setMyCerts}
          />
        )}
        {step === "location" && (
          <section data-testid="cp12-qual-location" className="space-y-4">
            <h2 className="text-xl font-semibold">Location & Travel</h2>
            <p className="text-sm text-slate-500">
              Lock §21 + §30 — your base location and work-auth countries are
              used to hard-filter search results. Set them accurately to be
              discovered by relevant Clients.
            </p>
            <ContractorLocationForm />
            <ContractorTravelForm />
          </section>
        )}
        {step === "verif" && (
          <section data-testid="cp12-qual-verif" className="space-y-4">
            <MyVerificationsPanel />
          </section>
        )}
      </div>
    </Layout>
  );
}

/* ===================== Tracks ===================== */

function TracksStep({ tracks, myTracks, saving, setSaving, onSaved }) {
  const [primary, setPrimary] = useState(myTracks.primary?.track_id || "");
  const [secondary, setSecondary] = useState(
    myTracks.secondary.map((s) => s.track_id)
  );

  const onToggleSecondary = (id) => {
    if (secondary.includes(id)) {
      setSecondary(secondary.filter((x) => x !== id));
    } else {
      setSecondary([...secondary, id]);
    }
  };

  const save = async () => {
    if (!primary) {
      toast.error("Choose a primary track");
      return;
    }
    if (secondary.includes(Number(primary))) {
      toast.error("Primary track cannot also be secondary");
      return;
    }
    setSaving(true);
    try {
      const updated = await api(
        "/me/contractor-profile/qualification/tracks",
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            primary_track_id: Number(primary),
            secondary_track_ids: secondary.map(Number),
          }),
        }
      );
      onSaved(updated);
      toast.success("Tracks saved");
    } catch (e) {
      toast.error(e?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section data-testid="cp12-qual-tracks">
      <h2 className="text-xl font-semibold mb-2">Industry Tracks</h2>
      <p className="text-sm text-slate-500 mb-4">
        Pick exactly ONE primary track (your professional centre of gravity).
        Add secondary tracks for other areas you also work in.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {tracks.map((t) => {
          const isPrimary = Number(primary) === t.id;
          const isSecondary = secondary.includes(t.id);
          return (
            <div
              key={t.id}
              className={
                "border rounded-lg p-4 " +
                (isPrimary
                  ? "border-slate-900 bg-slate-50"
                  : isSecondary
                  ? "border-emerald-500 bg-emerald-50"
                  : "border-slate-200")
              }
              data-testid={`cp12-qual-track-${t.slug}`}
            >
              <div className="font-medium">{t.display_name}</div>
              <div className="text-xs text-slate-500 mb-3">{t.slug}</div>
              <label className="flex items-center gap-2 mb-2 text-sm">
                <input
                  type="radio"
                  name="primary_track"
                  checked={isPrimary}
                  onChange={() => {
                    setPrimary(t.id);
                    if (secondary.includes(t.id)) {
                      setSecondary(secondary.filter((x) => x !== t.id));
                    }
                  }}
                  data-testid={`cp12-qual-track-${t.slug}-primary`}
                />
                Primary
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={isSecondary}
                  disabled={isPrimary}
                  onChange={() => onToggleSecondary(t.id)}
                  data-testid={`cp12-qual-track-${t.slug}-secondary`}
                />
                Secondary
              </label>
            </div>
          );
        })}
      </div>

      <div className="mt-6">
        <button
          onClick={save}
          disabled={saving}
          className="px-4 py-2 bg-slate-900 text-white rounded-md text-sm disabled:opacity-50"
          data-testid="cp12-qual-tracks-save"
        >
          {saving ? "Saving…" : "Save tracks"}
        </button>
      </div>
    </section>
  );
}

/* ===================== Segments ===================== */

function SegmentsStep({ segments, tracks, mySegments, saving, setSaving, onSaved }) {
  const [picked, setPicked] = useState(new Set(mySegments));
  const trackById = useMemo(() => {
    const m = {}; tracks.forEach((t) => { m[t.id] = t; }); return m;
  }, [tracks]);

  const grouped = useMemo(() => {
    const g = {};
    segments.forEach((s) => {
      if (!g[s.track_id]) g[s.track_id] = [];
      g[s.track_id].push(s);
    });
    return g;
  }, [segments]);

  const toggle = (id) => {
    const next = new Set(picked);
    if (next.has(id)) next.delete(id); else next.add(id);
    setPicked(next);
  };

  const save = async () => {
    setSaving(true);
    try {
      const updated = await api(
        "/me/contractor-profile/qualification/segments",
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ segment_ids: Array.from(picked) }),
        }
      );
      onSaved(updated.data.map((s) => s.segment_id));
      toast.success("Segments saved");
    } catch (e) {
      toast.error(e?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section data-testid="cp12-qual-segments">
      <h2 className="text-xl font-semibold mb-4">Segments</h2>
      {Object.entries(grouped).map(([tid, items]) => (
        <div key={tid} className="mb-6">
          <div className="text-sm font-medium text-slate-700 mb-2">
            {trackById[tid]?.display_name}
          </div>
          <div className="flex flex-wrap gap-2">
            {items.map((s) => {
              const active = picked.has(s.id);
              return (
                <button
                  key={s.id}
                  onClick={() => toggle(s.id)}
                  className={
                    "px-3 py-1.5 rounded-full text-sm border " +
                    (active
                      ? "bg-slate-900 text-white border-slate-900"
                      : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50")
                  }
                  data-testid={`cp12-qual-segment-${s.slug}`}
                >
                  {s.display_name}
                </button>
              );
            })}
          </div>
        </div>
      ))}
      <button
        onClick={save}
        disabled={saving}
        className="px-4 py-2 bg-slate-900 text-white rounded-md text-sm disabled:opacity-50"
        data-testid="cp12-qual-segments-save"
      >
        {saving ? "Saving…" : "Save segments"}
      </button>
    </section>
  );
}

/* ===================== Roles ===================== */

function RolesStep({ roles, tracks, myRoles, saving, setSaving, onSaved }) {
  const [draft, setDraft] = useState(myRoles);
  const trackById = useMemo(() => {
    const m = {}; tracks.forEach((t) => { m[t.id] = t; }); return m;
  }, [tracks]);

  const grouped = useMemo(() => {
    const g = {};
    roles.forEach((r) => {
      if (!g[r.track_id]) g[r.track_id] = [];
      g[r.track_id].push(r);
    });
    return g;
  }, [roles]);

  const toggle = (id) => {
    const idx = draft.findIndex((d) => d.role_id === id);
    if (idx >= 0) setDraft(draft.filter((d) => d.role_id !== id));
    else setDraft([...draft, { role_id: id, is_primary: false, years_in_role: null }]);
  };

  const setYears = (id, val) => {
    setDraft(draft.map((d) =>
      d.role_id === id ? { ...d, years_in_role: val === "" ? null : Number(val) } : d
    ));
  };

  const setPrimary = (id) => {
    setDraft(draft.map((d) => ({ ...d, is_primary: d.role_id === id })));
  };

  const save = async () => {
    setSaving(true);
    try {
      const updated = await api(
        "/me/contractor-profile/qualification/roles",
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ roles: draft }),
        }
      );
      onSaved(updated.data.map((r) => ({
        role_id: r.role_id, is_primary: r.is_primary,
        years_in_role: r.years_in_role,
      })));
      toast.success("Roles saved");
    } catch (e) {
      toast.error(e?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section data-testid="cp12-qual-roles">
      <h2 className="text-xl font-semibold mb-4">Professional Roles</h2>
      {Object.entries(grouped).map(([tid, items]) => (
        <div key={tid} className="mb-6">
          <div className="text-sm font-medium text-slate-700 mb-2">
            {trackById[tid]?.display_name}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {items.map((r) => {
              const claim = draft.find((d) => d.role_id === r.id);
              const active = !!claim;
              return (
                <div
                  key={r.id}
                  className={
                    "border rounded-md px-3 py-2 flex items-center justify-between text-sm " +
                    (active
                      ? "border-slate-900 bg-slate-50"
                      : "border-slate-200")
                  }
                  data-testid={`cp12-qual-role-${r.slug}`}
                >
                  <label className="flex items-center gap-2 flex-1">
                    <input
                      type="checkbox"
                      checked={active}
                      onChange={() => toggle(r.id)}
                    />
                    {r.display_name}
                  </label>
                  {active && (
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min="0"
                        step="0.5"
                        placeholder="yrs"
                        value={claim.years_in_role ?? ""}
                        onChange={(e) => setYears(r.id, e.target.value)}
                        className="w-16 border border-slate-300 rounded px-1 text-xs"
                        data-testid={`cp12-qual-role-${r.slug}-years`}
                      />
                      <label className="flex items-center gap-1 text-xs">
                        <input
                          type="radio"
                          name="role_primary"
                          checked={claim.is_primary}
                          onChange={() => setPrimary(r.id)}
                          data-testid={`cp12-qual-role-${r.slug}-primary`}
                        />
                        primary
                      </label>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
      <button
        onClick={save}
        disabled={saving}
        className="px-4 py-2 bg-slate-900 text-white rounded-md text-sm disabled:opacity-50"
        data-testid="cp12-qual-roles-save"
      >
        {saving ? "Saving…" : "Save roles"}
      </button>
    </section>
  );
}

/* ===================== Equipment ===================== */

function EquipmentStep({ equipmentTags, myEquipment, saving, setSaving, onSaved }) {
  const [draft, setDraft] = useState(myEquipment);

  const grouped = useMemo(() => {
    const g = {};
    equipmentTags.forEach((e) => {
      if (!g[e.category]) g[e.category] = [];
      g[e.category].push(e);
    });
    return g;
  }, [equipmentTags]);

  const find = (id) => draft.find((d) => d.equipment_tag_id === id);

  const toggle = (id) => {
    if (find(id)) {
      setDraft(draft.filter((d) => d.equipment_tag_id !== id));
    } else {
      setDraft([
        ...draft,
        { equipment_tag_id: id, project_count_tier: "1-2", subjective_level: null },
      ]);
    }
  };

  const setTier = (id, val) => {
    setDraft(draft.map((d) =>
      d.equipment_tag_id === id ? { ...d, project_count_tier: val } : d
    ));
  };

  const setLevel = (id, val) => {
    setDraft(draft.map((d) =>
      d.equipment_tag_id === id
        ? { ...d, subjective_level: val === "" ? null : val }
        : d
    ));
  };

  const save = async () => {
    setSaving(true);
    try {
      const updated = await api(
        "/me/contractor-profile/qualification/equipment",
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ equipment: draft }),
        }
      );
      onSaved(updated.data.map((e) => ({
        equipment_tag_id: e.equipment_tag_id,
        project_count_tier: e.project_count_tier,
        subjective_level: e.subjective_level,
      })));
      toast.success("Equipment saved");
    } catch (e) {
      toast.error(e?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section data-testid="cp12-qual-equipment">
      <h2 className="text-xl font-semibold mb-2">Equipment Experience</h2>
      <p className="text-sm text-slate-500 mb-4">
        Pick the equipment you actually work with. The project-count tier is
        the primary ranking signal for client searches. The subjective level
        is shown for context but doesn&apos;t affect ranking.
      </p>
      {Object.entries(grouped).map(([cat, items]) => (
        <div key={cat} className="mb-6">
          <div className="text-sm font-medium text-slate-700 mb-2 capitalize">
            {cat.replace(/_/g, " ")}
          </div>
          <div className="grid grid-cols-1 gap-2">
            {items.map((e) => {
              const claim = find(e.id);
              return (
                <div
                  key={e.id}
                  className={
                    "border rounded-md px-3 py-2 text-sm " +
                    (claim
                      ? "border-slate-900 bg-slate-50"
                      : "border-slate-200")
                  }
                  data-testid={`cp12-qual-equipment-${e.slug}`}
                >
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={!!claim}
                      onChange={() => toggle(e.id)}
                    />
                    <span className="flex-1">
                      <span className="font-medium">{e.manufacturer}</span>{" "}
                      {e.model_or_family}
                    </span>
                  </label>
                  {claim && (
                    <div className="flex flex-wrap items-center gap-3 mt-2 ml-6">
                      <label className="text-xs text-slate-600">
                        Projects:
                        <select
                          value={claim.project_count_tier}
                          onChange={(ev) => setTier(e.id, ev.target.value)}
                          className="ml-1 border border-slate-300 rounded px-1 text-xs"
                          data-testid={`cp12-qual-equipment-${e.slug}-tier`}
                        >
                          {TIERS.map((t) => (
                            <option key={t} value={t}>{t}</option>
                          ))}
                        </select>
                      </label>
                      <label className="text-xs text-slate-600">
                        Level:
                        <select
                          value={claim.subjective_level || ""}
                          onChange={(ev) => setLevel(e.id, ev.target.value)}
                          className="ml-1 border border-slate-300 rounded px-1 text-xs"
                          data-testid={`cp12-qual-equipment-${e.slug}-level`}
                        >
                          <option value="">—</option>
                          {SUBJECTIVE.map((l) => (
                            <option key={l} value={l}>{l}</option>
                          ))}
                        </select>
                      </label>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
      <button
        onClick={save}
        disabled={saving}
        className="px-4 py-2 bg-slate-900 text-white rounded-md text-sm disabled:opacity-50"
        data-testid="cp12-qual-equipment-save"
      >
        {saving ? "Saving…" : "Save equipment"}
      </button>
    </section>
  );
}

/* ===================== Certifications ===================== */

function CertificationsStep({ certifications, myCerts, saving, setSaving, onSaved }) {
  const [draft, setDraft] = useState(myCerts);
  const find = (id) => draft.find((d) => d.certification_id === id);

  const toggle = (id) => {
    if (find(id)) setDraft(draft.filter((d) => d.certification_id !== id));
    else setDraft([
      ...draft,
      { certification_id: id, issued_at: null, expires_at: null,
        credential_id: null, verification_url: null },
    ]);
  };
  const upd = (id, field, val) => {
    setDraft(draft.map((d) =>
      d.certification_id === id
        ? { ...d, [field]: val === "" ? null : val }
        : d
    ));
  };

  const save = async () => {
    setSaving(true);
    try {
      const updated = await api(
        "/me/contractor-profile/qualification/certifications",
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ certifications: draft }),
        }
      );
      onSaved(updated.data.map((c) => ({
        certification_id: c.certification_id,
        issued_at: c.issued_at, expires_at: c.expires_at,
        credential_id: c.credential_id, verification_url: c.verification_url,
      })));
      toast.success("Certifications saved");
    } catch (e) {
      toast.error(e?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section data-testid="cp12-qual-certs">
      <h2 className="text-xl font-semibold mb-4">Certifications</h2>
      <div className="space-y-2">
        {certifications.map((c) => {
          const claim = find(c.id);
          return (
            <div
              key={c.id}
              className={
                "border rounded-md px-3 py-2 text-sm " +
                (claim
                  ? "border-slate-900 bg-slate-50"
                  : "border-slate-200")
              }
              data-testid={`cp12-qual-cert-${c.slug}`}
            >
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={!!claim}
                  onChange={() => toggle(c.id)}
                />
                <span className="flex-1">
                  <span className="font-medium">{c.display_name}</span>
                  <span className="ml-2 text-xs text-slate-500">
                    {c.issuing_body}{c.expiry_required ? " · expiry required" : ""}
                  </span>
                </span>
              </label>
              {claim && (
                <div className="flex flex-wrap items-center gap-3 mt-2 ml-6">
                  <label className="text-xs text-slate-600">
                    Issued:
                    <input
                      type="date"
                      value={claim.issued_at || ""}
                      onChange={(e) => upd(c.id, "issued_at", e.target.value)}
                      className="ml-1 border border-slate-300 rounded px-1 text-xs"
                      data-testid={`cp12-qual-cert-${c.slug}-issued`}
                    />
                  </label>
                  <label className="text-xs text-slate-600">
                    Expires:
                    <input
                      type="date"
                      value={claim.expires_at || ""}
                      onChange={(e) => upd(c.id, "expires_at", e.target.value)}
                      className="ml-1 border border-slate-300 rounded px-1 text-xs"
                      data-testid={`cp12-qual-cert-${c.slug}-expires`}
                    />
                  </label>
                  <label className="text-xs text-slate-600">
                    Credential ID:
                    <input
                      type="text"
                      value={claim.credential_id || ""}
                      onChange={(e) => upd(c.id, "credential_id", e.target.value)}
                      className="ml-1 border border-slate-300 rounded px-1 text-xs w-32"
                      data-testid={`cp12-qual-cert-${c.slug}-credid`}
                    />
                  </label>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="mt-4">
        <button
          onClick={save}
          disabled={saving}
          className="px-4 py-2 bg-slate-900 text-white rounded-md text-sm disabled:opacity-50"
          data-testid="cp12-qual-certs-save"
        >
          {saving ? "Saving…" : "Save certifications"}
        </button>
      </div>
    </section>
  );
}
